import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Users, AlertCircle, Calendar, TrendingUp, ChevronRight, Search } from 'lucide-react';
import { useLang } from '../i18n';
import { api } from '../api';
import { cn } from '../lib/utils';

const fmtDuration = (mins) => `${Math.floor(mins / 60)}h${mins % 60 ? (mins % 60) : ''}`;
const fmtTime = (iso) => { try { return format(parseISO(iso), 'HH:mm'); } catch { return iso; } };
const fmtDate = (iso, locale) => { try { return format(parseISO(iso), 'EEE d MMM', { locale }); } catch { return iso; } };

function ClassCard({ cls, onClick }) {
  const isGroup = cls.type === 'group';
  return (
    <div
      onClick={onClick}
      className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm hover:shadow-md transition-all cursor-pointer active:scale-[0.98]"
      data-testid="class-card"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <span className={cn(
            'text-xs font-bold px-2 py-0.5 rounded-full',
            isGroup ? 'bg-[#0288D1]/10 text-[#0288D1]' : 'bg-[#26A69A]/10 text-[#26A69A]'
          )}>
            {isGroup ? 'Collectif' : 'Privé'}
          </span>
          <div className="mt-1.5 font-bold text-slate-900 text-lg font-manrope">{fmtTime(cls.datetime_start)}</div>
          {cls.title && <div className="text-sm font-medium text-slate-700">{cls.title}</div>}
          <div className="text-xs text-slate-500 mt-0.5">
            {cls.location?.venue_name} · {cls.location?.city}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-sm font-semibold text-slate-700">{fmtDuration(cls.duration_minutes)}</div>
          <div className="text-xs text-slate-400">{cls.student_count || 0} élève(s)</div>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const locale = lang === 'fr' ? fr : undefined;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allStudents, setAllStudents] = useState([]);
  const [studentSearch, setStudentSearch] = useState('');
  const [showDrop, setShowDrop] = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    api.get('/dashboard').then(setData).catch(() => {}).finally(() => setLoading(false));
    api.get('/students').then(setAllStudents).catch(() => {});
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => { if (searchRef.current && !searchRef.current.contains(e.target)) setShowDrop(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const matchedStudents = studentSearch.trim().length >= 1
    ? allStudents.filter(s =>
        `${s.first_name} ${s.last_name}`.toLowerCase().includes(studentSearch.toLowerCase())
      ).slice(0, 8)
    : [];

  if (loading) return (
    <div className="flex items-center justify-center h-64 text-slate-400">{t('common.loading')}</div>
  );

  const stats = data?.stats || {};
  const today = data?.today_classes || [];
  const upcoming = data?.upcoming_classes || [];
  const debtors = data?.debtors || [];

  return (
    <div className="max-w-2xl mx-auto lg:max-w-4xl space-y-6" data-testid="dashboard">
      <div className="pt-2">
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tighter text-slate-900 font-manrope">
          {t('dashboard.title')}
        </h1>
        <p className="text-slate-500 text-sm mt-1">
          {format(new Date(), 'EEEE d MMMM yyyy', { locale })}
        </p>
      </div>

      {/* Student search bar */}
      <div ref={searchRef} className="relative" data-testid="student-search-container">
        <div className="relative">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input
            value={studentSearch}
            onChange={e => { setStudentSearch(e.target.value); setShowDrop(true); }}
            onFocus={() => setShowDrop(true)}
            placeholder="Rechercher un élève..."
            className="w-full h-11 pl-10 pr-4 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0288D1]/20 focus:border-[#0288D1] shadow-sm"
            data-testid="student-search-input"
          />
        </div>
        {showDrop && matchedStudents.length > 0 && (
          <div className="absolute z-20 w-full bg-white rounded-xl border border-slate-200 shadow-xl mt-1 overflow-hidden" data-testid="student-search-dropdown">
            {matchedStudents.map(s => (
              <button
                key={s.id}
                onMouseDown={() => { navigate(`/students/${s.id}`); setStudentSearch(''); setShowDrop(false); }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left hover:bg-slate-50 transition-colors border-b border-slate-50 last:border-0"
                data-testid="student-search-result"
              >
                <Avatar name={`${s.first_name} ${s.last_name}`} size="sm" />
                <div>
                  <span className="font-semibold text-slate-800">{s.first_name} {s.last_name}</span>
                  {s.role && (
                    <span className={cn('ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded-full',
                      s.role === 'leader' ? 'bg-blue-100 text-blue-600' : 'bg-pink-100 text-pink-600')}>
                      {s.role === 'leader' ? 'Leader' : 'Follower'}
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Stats Bento */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Users} label={t('nav.students')} value={stats.total_students || 0} color="blue" testId="stat-students" />
        <StatCard icon={Calendar} label={t('dashboard.today')} value={stats.today_class_count || 0} color="teal" testId="stat-today" />
        <StatCard icon={AlertCircle} label={t('dashboard.debtors')} value={stats.debtors_count || 0} color="red" testId="stat-debtors" />
        <StatCard icon={TrendingUp} label={t('dashboard.totalOwed')} value={`${stats.total_owed || 0} CHF`} color="orange" testId="stat-owed" />
      </div>

      {/* Today's classes */}
      <section>
        <SectionHeader title={t('dashboard.today')} count={today.length} />
        {today.length === 0 ? (
          <EmptyState text={t('dashboard.noClasses')} />
        ) : (
          <div className="space-y-3">
            {today.map(cls => (
              <ClassCard key={cls.id} cls={cls} onClick={() => navigate('/planning')} />
            ))}
          </div>
        )}
      </section>

      {/* Debtors */}
      {debtors.length > 0 && (
        <section>
          <SectionHeader title={t('dashboard.debts')} count={debtors.length} />
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
            {debtors.map((d, i) => (
              <div
                key={d.student.id}
                onClick={() => navigate(`/students/${d.student.id}`)}
                className={cn(
                  'flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-slate-50 active:bg-slate-100 transition-colors',
                  i < debtors.length - 1 && 'border-b border-slate-100'
                )}
                data-testid="debtor-row"
              >
                <div className="flex items-center gap-3">
                  <Avatar name={`${d.student.first_name} ${d.student.last_name}`} />
                  <div>
                    <div className="font-semibold text-slate-900 text-sm">
                      {d.student.first_name} {d.student.last_name}
                    </div>
                    <div className="text-xs text-slate-500">{d.package?.type === 'group' ? 'Collectif' : 'Privé'}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-red-500 text-sm">{d.remaining} CHF</span>
                  <ChevronRight size={14} className="text-slate-300" />
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Upcoming */}
      {upcoming.length > 0 && (
        <section>
          <SectionHeader title={t('dashboard.upcoming')} count={upcoming.length} />
          <div className="space-y-2">
            {upcoming.slice(0, 5).map(cls => (
              <div
                key={cls.id}
                className="bg-white rounded-xl px-4 py-3 border border-slate-100 flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <div className={cn(
                    'w-2 h-2 rounded-full',
                    cls.type === 'group' ? 'bg-[#0288D1]' : 'bg-[#26A69A]'
                  )} />
                  <div>
                    <span className="text-sm font-semibold text-slate-800">
                      {fmtDate(cls.datetime_start, locale)} · {fmtTime(cls.datetime_start)}
                    </span>
                    <div className="text-xs text-slate-400">{cls.location?.venue_name} · {cls.location?.city}</div>
                  </div>
                </div>
                <span className="text-xs text-slate-400">{fmtDuration(cls.duration_minutes)}</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color, testId }) {
  const colors = {
    blue: 'bg-[#0288D1]/10 text-[#0288D1]',
    teal: 'bg-[#26A69A]/10 text-[#26A69A]',
    red: 'bg-red-50 text-red-500',
    orange: 'bg-amber-50 text-amber-600',
  };
  return (
    <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm" data-testid={testId}>
      <div className={cn('w-9 h-9 rounded-xl flex items-center justify-center mb-3', colors[color])}>
        <Icon size={18} />
      </div>
      <div className="text-2xl font-extrabold text-slate-900 font-manrope">{value}</div>
      <div className="text-xs text-slate-500 mt-0.5">{label}</div>
    </div>
  );
}

function SectionHeader({ title, count }) {
  return (
    <div className="flex items-center justify-between mb-3">
      <h2 className="text-lg font-bold text-slate-900 font-manrope">{title}</h2>
      {count > 0 && (
        <span className="text-xs font-semibold bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{count}</span>
      )}
    </div>
  );
}

function EmptyState({ text }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-8 text-center text-slate-400 text-sm">
      {text}
    </div>
  );
}

export function Avatar({ name, size = 'md' }) {
  const initials = name ? name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() : '?';
  const colors = ['bg-[#0288D1]', 'bg-[#26A69A]', 'bg-purple-500', 'bg-amber-500', 'bg-pink-500'];
  const color = colors[(name?.charCodeAt(0) || 0) % colors.length];
  const sz = size === 'sm' ? 'w-8 h-8 text-xs' : size === 'lg' ? 'w-12 h-12 text-base' : 'w-9 h-9 text-sm';
  return (
    <div className={cn('rounded-full flex items-center justify-center font-bold text-white shrink-0', color, sz)}>
      {initials}
    </div>
  );
}
